import { Box, Select, SelectChangeEvent, Typography } from "@mui/material";
import { EmailTemplateEventEnum } from "helpers/constants";
import ENFlag from "images/eng_flag.png";
import ExQR from "images/exQR.png";
import FRFlag from "images/fr_flag.png";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TEMPLATE_TITLE_MAP } from "./EmailPreview.constants";
import {
  GreenText,
  HeaderBox,
  ImageFlag,
  ImageQR,
  InfoSection,
  StyledStrong,
  StyleMenuItem,
  TypoDescription,
  TypoTitle,
  TypoTitle2,
  WrapperImageQr,
} from "./EmailPreview.style";
import { EmailPreviewProps, Language } from "./EmailPreview.type";

export default function EmailPreview({ template }: EmailPreviewProps) {
  const [language, setLanguage] = useState<Language>("fr");

  const { triggerEvent, includeQrCode, translations } = template;

  const { t } = useTranslation("emailing");

  const title = useMemo(() => {
    return TEMPLATE_TITLE_MAP[triggerEvent as EmailTemplateEventEnum];
  }, [triggerEvent]);

  const content = useMemo(() => {
    return (
      translations?.find(
        (t) => t.languageCode === language && t.subject === "content",
      )?.content || ""
    );
  }, [template, language]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value as Language);
  };

  const renderTemplateSpecificContent = () => {
    switch (triggerEvent) {
      case EmailTemplateEventEnum.BOOKING_CONFIRMATION:
        return (
          <TypoTitle variant="body1">
            {t("preview.thankYouBookings", {
              lng: language,
            })}
            <GreenText variant="caption">
              {t("insert", { lng: language })} clients.company_name.
            </GreenText>
          </TypoTitle>
        );

      case EmailTemplateEventEnum.BOOKING_CANCELLATION:
        return (
          <TypoTitle variant="body1">
            {t("preview.reservationExperience", {
              lng: language,
            })}
            <GreenText variant="caption">
              {t("insert", { lng: language })} activities.title
            </GreenText>{" "}
            {t("preview.canceledBookings", {
              lng: language,
            })}
            <GreenText variant="caption">
              {t("insert", { lng: language })}{" "}
              activity_visitor_bookings.from{" "}
            </GreenText>{" "}
            {t("preview.canceledBookingsNext", {
              lng: language,
            })}
          </TypoTitle>
        );

      default:
        return null;
    }
  };

  const renderBookingDetails = () => {
    // if (type === EmailTemplateEventEnum.LOYALTY) return null;

    return (
      <>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t("preview.bookingDetail", { lng: language })}
        </Typography>

        <Box sx={{ ml: 3, p: 0 }}>
          {(triggerEvent === EmailTemplateEventEnum.BOOKING_CANCELLATION ||
            triggerEvent === EmailTemplateEventEnum.BOOKING_CONFIRMATION) && (
            <Typography sx={{ mb: 1 }}>
              &#8226;
              <StyledStrong>
                {t("preview.reservationNumber", { lng: language })}
              </StyledStrong>{" "}
              <GreenText variant="caption">
                {t("insert", { lng: language })} order.id
              </GreenText>
            </Typography>
          )}
          <Typography sx={{ mb: 1 }}>
            &#8226;
            <StyledStrong>
              {t("preview.activity", { lng: language })}
            </StyledStrong>{" "}
            <GreenText variant="caption">
              {t("insert", { lng: language })} activities.title
            </GreenText>
          </Typography>
          <Typography sx={{ mb: 1 }}>
            &#8226;
            <StyledStrong>
              {t("preview.date", { lng: language })}
            </StyledStrong>{" "}
            <GreenText variant="caption">
              {t("insert", { lng: language })}{" "}
              activity_visitor_bookings.from{" "}
            </GreenText>
          </Typography>
          <Typography sx={{ mb: 1 }}>
            &#8226;
            <StyledStrong>
              {t("preview.address", { lng: language })}
            </StyledStrong>{" "}
            <GreenText variant="caption">
              {t("insert", { lng: language })} activities.adress
            </GreenText>
          </Typography>
          <Typography sx={{ mb: 1 }}>
            &#8226;
            <StyledStrong>
              {t("preview.number", { lng: language })}
            </StyledStrong>{" "}
            <GreenText variant="caption">
              {t("insert", { lng: language })} nb_of_units{" "}
            </GreenText>
          </Typography>
          <Typography sx={{ mb: 1 }}>
            &#8226;
            <StyledStrong>
              {t("preview.total", { lng: language })}
            </StyledStrong>{" "}
            <GreenText variant="caption">
              {t("insert", { lng: language })} total amount with discount
            </GreenText>
          </Typography>
          {(triggerEvent === EmailTemplateEventEnum.BOOKING_CANCELLATION ||
            triggerEvent === EmailTemplateEventEnum.BOOKING_CONFIRMATION) && (
            <Typography sx={{ mb: 1 }}>
              &#8226;
              <StyledStrong>
                {t("preview.receptionPhone", { lng: language })}
              </StyledStrong>{" "}
              <GreenText variant="caption">
                {t("insert", { lng: language })}{" "}
                activites.activity_phone_number{" "}
              </GreenText>
            </Typography>
          )}
        </Box>
      </>
    );
  };

  const renderQRCode = () => {
    if (
      triggerEvent !== EmailTemplateEventEnum.BOOKING_CONFIRMATION ||
      !includeQrCode
    )
      return null;

    return (
      <>
        <Typography variant="body1" sx={{ mb: 1, textAlign: "center" }}>
          <strong>{t("preview.qrTitle", { lng: language })}</strong>
        </Typography>
        <WrapperImageQr>
          <ImageQR src={ExQR} alt="Example QR" />
        </WrapperImageQr>
      </>
    );
  };

  const renderContactInfo = () => (
    <InfoSection sx={{ backgroundColor: "#f0f8ff" }}>
      <TypoTitle2>
        {t("preview.establishmentContact", { lng: language })}
      </TypoTitle2>
      <Typography sx={{ mb: 1 }}>
        {t("preview.yourEmail", { lng: language })}{" "}
        <GreenText variant="caption">
          {t("insert", { lng: language })} activities.activity_email{" "}
        </GreenText>
      </Typography>
      <Typography sx={{ mb: 2 }}>
        {t("preview.establishmentTelephone", { lng: language })}{" "}
        <GreenText variant="caption">
          {t("insert", { lng: language })} clients.contact_phone_number{" "}
        </GreenText>
      </Typography>
      <TypoTitle2>{t("preview.yourContact", { lng: language })}</TypoTitle2>
      <Typography sx={{ mb: 1 }}>
        {t("preview.yourEmail", { lng: language })}{" "}
        <GreenText variant="caption">
          {t("insert", { lng: language })} visitors.email{" "}
        </GreenText>
      </Typography>
      <Typography sx={{ mb: 1 }}>
        {t("preview.yourTelephone", { lng: language })}{" "}
        <GreenText variant="caption">
          {t("insert", { lng: language })} visitors.phone_number
        </GreenText>
      </Typography>
    </InfoSection>
  );

  return (
    <div>
      <h2 className="title">{t("emailing.preview")}</h2>
      <Select
        value={language}
        onChange={handleLanguageChange}
        style={{ marginBottom: 10 }}
      >
        <StyleMenuItem value="fr">
          <ImageFlag src={FRFlag} alt="FR" /> Français (FR)
        </StyleMenuItem>
        <StyleMenuItem value="en">
          <ImageFlag src={ENFlag} alt="EN" /> English (EN)
        </StyleMenuItem>
      </Select>

      {!!triggerEvent && (
        <>
          <div>
            <HeaderBox>
              <Typography variant="h6" style={{ textAlign: "center" }}>
                {t(title, { lng: language })}
              </Typography>
            </HeaderBox>

            <TypoTitle variant="body1">
              {t("preview.hallo", { lng: language })},{" "}
              <GreenText variant="caption">
                {t("insert", { lng: language })} visitors.full_name
              </GreenText>
            </TypoTitle>

            {renderTemplateSpecificContent()}

            {content && <GreenText variant="body1">{content}</GreenText>}

            {renderBookingDetails()}
            {renderQRCode()}
            {renderContactInfo()}

            <TypoDescription sx={{ mt: 3 }}>
              {t("preview.haveANiceDay", { lng: language })}
            </TypoDescription>
            <TypoDescription sx={{ mt: 1 }}>
              {t("preview.desc")}
            </TypoDescription>
          </div>
        </>
      )}
    </div>
  );
}
